import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, ArrowRight, Star } from 'lucide-react';
const galleryItems = [{
  id: 1,
  procedure: 'Lipo 360',
  beforeImage: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=600&h=400&fit=crop&sat=-100',
  afterImage: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=600&h=400&fit=crop'
}, {
  id: 2,
  procedure: 'Tummy Tuck',
  beforeImage: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=600&h=400&fit=crop&sat=-100',
  afterImage: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=600&h=400&fit=crop'
}, {
  id: 3,
  procedure: 'BBL',
  beforeImage: 'https://images.unsplash.com/photo-1518611012118-696072aa579a?w=600&h=400&fit=crop&sat=-100',
  afterImage: 'https://images.unsplash.com/photo-1518611012118-696072aa579a?w=600&h=400&fit=crop'
}, {
  id: 4,
  procedure: 'Mommy Makeover',
  beforeImage: 'https://images.unsplash.com/photo-1515377905703-c4788e51af15?w=600&h=400&fit=crop&sat=-100',
  afterImage: 'https://images.unsplash.com/photo-1515377905703-c4788e51af15?w=600&h=400&fit=crop'
}];
interface BeforeAfterGalleryProps {
  onCtaClick: () => void;
}
export function BeforeAfterGallery({
  onCtaClick
}: BeforeAfterGalleryProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const nextSlide = () => {
    setCurrentIndex(prev => (prev + 1) % galleryItems.length);
  };
  const prevSlide = () => {
    setCurrentIndex(prev => (prev - 1 + galleryItems.length) % galleryItems.length);
  };
  return <section className="w-full py-20 lg:py-24 bg-gradient-to-br from-brand-navy via-[#0d2a52] to-brand-deep relative overflow-hidden" data-id="element-1">
      {/* Premium decorative elements */}
      <div className="absolute inset-0" data-id="element-2">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-brand-gold/10 rounded-full blur-3xl" data-id="element-3" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-brand-primary/10 rounded-full blur-3xl" data-id="element-4" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-5">
        {/* Section header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-6">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-gold/20 backdrop-blur-sm rounded-full mb-4" data-id="element-7">
            <Star className="w-4 h-4 text-brand-gold fill-brand-gold" data-id="element-8" />
            <span className="text-brand-gold font-semibold text-sm uppercase tracking-wider" data-id="element-9">
              Real Results
            </span>
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6" data-id="element-10">
            Real Patients.{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-primary to-brand-gold" data-id="element-11">
              Real Transformations.
            </span>
          </h2>
          <p className="text-xl text-white/70 max-w-2xl mx-auto" data-id="element-12">
            See the life-changing results our patients have achieved
          </p>
        </motion.div>

        {/* Gallery slider */}
        <div className="relative max-w-5xl mx-auto" data-id="element-13">
          <AnimatePresence mode="wait" data-id="element-14">
            <motion.div key={currentIndex} initial={{
            opacity: 0,
            x: 50
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -50
          }} transition={{
            duration: 0.4
          }} className="bg-white rounded-3xl overflow-hidden shadow-2xl ring-2 ring-brand-gold/20" data-id="element-15">
              {/* Procedure label with premium styling */}
              <div className="bg-gradient-to-r from-brand-navy via-brand-deep to-brand-navy px-6 py-5 border-b-2 border-brand-gold/30" data-id="element-16">
                <p className="text-white font-bold text-center text-xl flex items-center justify-center gap-2" data-id="element-17">
                  <Star className="w-5 h-5 text-brand-gold fill-brand-gold" data-id="element-18" />
                  {galleryItems[currentIndex].procedure}
                </p>
              </div>

              {/* Before/After images */}
              <div className="grid grid-cols-2" data-id="element-19">
                <div className="relative" data-id="element-20">
                  <img src={galleryItems[currentIndex].beforeImage} alt={`Before ${galleryItems[currentIndex].procedure}`} className="w-full h-80 sm:h-[28rem] object-cover" data-id="element-21" />
                  <div className="absolute bottom-6 left-6 px-6 py-3 bg-black/80 backdrop-blur-md rounded-2xl border border-white/20" data-id="element-22">
                    <span className="text-white text-base font-bold" data-id="element-23">
                      Before
                    </span>
                  </div>
                </div>
                <div className="relative" data-id="element-24">
                  <img src={galleryItems[currentIndex].afterImage} alt={`After ${galleryItems[currentIndex].procedure}`} className="w-full h-80 sm:h-[28rem] object-cover" data-id="element-25" />
                  <div className="absolute bottom-6 right-6 px-6 py-3 bg-gradient-to-r from-brand-primary to-brand-gold backdrop-blur-md rounded-2xl shadow-xl" data-id="element-26">
                    <span className="text-white text-base font-bold" data-id="element-27">
                      After
                    </span>
                  </div>
                </div>
              </div>

              {/* Caption */}
              <div className="px-6 py-5 bg-gradient-to-r from-brand-aqua/30 to-brand-gold/10 border-t border-brand-gold/20" data-id="element-28">
                <p className="text-center text-brand-navy font-semibold flex items-center justify-center gap-2" data-id="element-29">
                  <Star className="w-4 h-4 text-brand-gold fill-brand-gold" data-id="element-30" />
                  Real patient treated at Dermaclinic
                  <Star className="w-4 h-4 text-brand-gold fill-brand-gold" data-id="element-31" />
                </p>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Navigation buttons - Larger & Premium */}
          <button onClick={prevSlide} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-6 sm:-translate-x-8 w-16 h-16 bg-gradient-to-br from-white to-brand-aqua rounded-full shadow-2xl flex items-center justify-center text-brand-navy transition-all duration-300 hover:scale-110 hover:shadow-brand-gold/50 border-2 border-brand-gold/30" aria-label="Previous slide" data-id="element-32">
            <ChevronLeft className="w-8 h-8" data-id="element-33" />
          </button>
          <button onClick={nextSlide} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-6 sm:translate-x-8 w-16 h-16 bg-gradient-to-br from-white to-brand-aqua rounded-full shadow-2xl flex items-center justify-center text-brand-navy transition-all duration-300 hover:scale-110 hover:shadow-brand-gold/50 border-2 border-brand-gold/30" aria-label="Next slide" data-id="element-34">
            <ChevronRight className="w-8 h-8" data-id="element-35" />
          </button>

          {/* Dots indicator with gold */}
          <div className="flex justify-center gap-3 mt-10" data-id="element-36">
            {galleryItems.map((_, index) => <button key={index} onClick={() => setCurrentIndex(index)} className={`h-3 rounded-full transition-all duration-300 ${index === currentIndex ? 'bg-brand-gold w-12 shadow-lg shadow-brand-gold/50' : 'bg-white/30 w-3 hover:bg-white/50'}`} aria-label={`Go to slide ${index + 1}`} data-id="element-37" />)}
          </div>
        </div>

        {/* Premium CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.3
      }} className="text-center mt-16" data-id="element-38">
          <button onClick={onCtaClick} className="group inline-flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-brand-gold to-[#c99435] text-brand-navy font-bold text-xl rounded-full transition-all duration-300 shadow-2xl shadow-brand-gold/40 hover:shadow-brand-gold/60 hover:scale-105" aria-label="Get your personalized evaluation" data-id="element-39">
            Get Your Personalized Evaluation
            <ArrowRight className="w-6 h-6 transition-transform duration-300 group-hover:translate-x-1" data-id="element-40" />
          </button>
        </motion.div>
      </div>
    </section>;
}